import { Add, ExportSquare, TickCircle } from 'iconsax-react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import { EConsentMethod, ERunReportStep } from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import { setConsent } from '@/store/api/RunReport/RunReport.slice'
import { fileToBase64 } from '@/utils/binary'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'

const MAX_PDF_SIZE_BYTES = 6_000_000

export const ConsentRequired = () => {
  useRunReportGuard(ERunReportStep.ConsentRequired)
  const { current, total } = useRunReportWizardStep(
    ERunReportStep.ConsentRequired,
  )
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const hasUploadedFile =
    typeof slice.consent.file === 'string' && slice.consent.file.length > 0
  const isDelegateSelected = slice.consent.method === EConsentMethod.Delegate

  const continueToNextStep = () => {
    navigate(AppRoutes.runReportSubjectForm)
  }

  const handleUpload = (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return
    fileToBase64(acceptedFiles[0], (base64File) => {
      dispatch(
        setConsent({
          ...slice.consent,
          method: EConsentMethod.Upload,
          file: base64File,
          fileName: acceptedFiles[0].name,
        }),
      )
      continueToNextStep()
    })
  }

  const handleUploadRejected = () => {
    toast.error(t('dashboard.runReport.consentRequired.fileError'))
  }

  const { getInputProps, open: openFilePicker } = useDropzone({
    onDrop: handleUpload,
    onDropRejected: handleUploadRejected,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: MAX_PDF_SIZE_BYTES,
    noClick: true,
  })

  const handleDelegate = () => {
    dispatch(
      setConsent({
        ...slice.consent,
        method: EConsentMethod.Delegate,
        file: '',
        fileName: '',
      }),
    )
    continueToNextStep()
  }

  const handleBack = () => navigate(AppRoutes.runReportSearchForm)

  const progressPercent = (current / total) * 100

  const renderUploadCard = () => (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-neutralColor5 p-6 text-center">
      <p className="text-xl font-medium text-neutralColor">
        {t('dashboard.runReport.consentRequired.uploadCardTitle')}
      </p>
      <p className="text-base text-neutralColor80">
        {t('dashboard.runReport.consentRequired.uploadCardDescription')}
      </p>
      <Button
        type="button"
        variant="secondary"
        className="mt-2 gap-2"
        onClick={openFilePicker}
      >
        {hasUploadedFile
          ? t('dashboard.runReport.consentRequired.uploadCardChangeFile')
          : t('dashboard.runReport.consentRequired.uploadCardButton')}
        {!hasUploadedFile && <Add size="16" color="currentColor" />}
      </Button>
      {hasUploadedFile && (
        <div className="mt-2 flex items-center gap-2 text-sm text-dynamicBlue">
          <TickCircle
            size="16"
            color={weCheckPalette.dynamicBlue}
            variant="Bold"
          />
          <span className="truncate">
            {slice.consent.fileName ||
              t('dashboard.runReport.consentRequired.uploadCardFileLoaded')}
          </span>
        </div>
      )}
    </div>
  )

  const renderDelegateCard = () => (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-neutralColor5 p-6 text-center">
      <p className="text-xl font-medium text-neutralColor">
        {t('dashboard.runReport.consentRequired.delegateCardTitle')}
      </p>
      <p className="text-base text-neutralColor80">
        {t('dashboard.runReport.consentRequired.delegateCardDescription')}
      </p>
      <Button
        type="button"
        variant="secondary"
        className="mt-2 gap-2"
        onClick={handleDelegate}
      >
        {t('dashboard.runReport.consentRequired.delegateCardButton')}
        <ExportSquare size="16" color="currentColor" />
      </Button>
      {isDelegateSelected && (
        <div className="mt-2 flex items-center gap-2 text-sm text-dynamicBlue">
          <TickCircle
            size="16"
            color={weCheckPalette.dynamicBlue}
            variant="Bold"
          />
          <span>
            {t('dashboard.runReport.consentRequired.delegateCardSelected')}
          </span>
        </div>
      )}
    </div>
  )

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <input {...getInputProps()} />

      <div className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8">
        <div className="hidden h-full flex-col lg:flex lg:flex-1">
          <div className="flex flex-1 flex-col justify-center gap-3 px-3">
            <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
              {t('dashboard.runReport.consentRequired.title')}
            </h2>
            <p className="text-base text-neutralColor">
              {t('dashboard.runReport.consentRequired.subtitle')}
            </p>

            <div className="mt-2 h-5 w-full overflow-hidden rounded-full bg-neutralColor30">
              <div
                className="h-full rounded-full bg-primaryColor transition-[width] duration-200"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <RunReportStepIndicator
              current={current}
              total={total}
              format="verbose"
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 lg:h-full lg:overflow-hidden lg:p-0">
          <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-2xl bg-baseColorHighlight p-4 lg:h-full lg:min-h-0 lg:gap-8 lg:p-6">
            {renderUploadCard()}
            {renderDelegateCard()}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-medium leading-tight text-neutralColor">
              {t('dashboard.runReport.consentRequired.title')}
            </h2>
            <p className="text-sm text-neutralColor">
              {t('dashboard.runReport.consentRequired.subtitle')}
            </p>
          </div>
        </div>
      </div>
    </WeScreen>
  )
}
